// Shared input-sanitation + serialization helpers for the Catalog Manager item
// dialog and the public catalog detail renderer.
//
// Delimiter conventions (match _latest.csv / export):
//   • Description paragraphs  -> ";"   (lists are NOT allowed here)
//   • Features                -> ";"   (one per line; pasted lists accepted)
//   • Parts, Materials        -> "|"   (one per line; pasted lists accepted)
//
// Storage stays as it is in Mongo: features/parts/materials are arrays,
// description is a ";"-joined string. These helpers normalize free-form paste
// (bulleted / numbered / dashed lists) into that shape, and report rejections.

// A leading bullet/number marker, e.g. "• ", "- ", "* ", "1. ", "1) ", "(1) ".
// Requires trailing whitespace so things like "-20°C" or "1.5 gal" are NOT
// treated as list markers.
const LIST_MARKER = /^(?:[-*•·‣◦]|\(?\d+[.)])\s+/;

function stripMarker(line) {
  return line.replace(LIST_MARKER, "").trim();
}

// Description: each non-empty input line becomes a paragraph, joined with ";".
// Reject if any line looks like a list item (use the Features field for lists).
// A literal ";" the user types is kept (acts as an extra paragraph break).
export function sanitizeDescription(raw) {
  const errors = [];
  const lines = String(raw ?? "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const offending = lines.filter((l) => LIST_MARKER.test(l));
  if (offending.length) {
    errors.push(
      `Lists aren't allowed in Description; use the Features field. Offending line${offending.length > 1 ? "s" : ""}: ${offending
        .map((l) => `"${l}"`)
        .join(", ")}`
    );
  }
  return { value: lines.join(";"), errors };
}

// Features / Parts / Materials: normalize a pasted list into an array.
// Primary split is one item per line; a single inline line delimited by the
// target char is also accepted. Leading list markers are stripped. Rejects
// only when a multi-line item still contains the delimiter, or when there was
// content but nothing usable survived.
export function sanitizeList(raw, delimiter) {
  const errors = [];
  const text = String(raw ?? "");
  const hadContent = text.trim().length > 0;
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  let items;
  if (lines.length <= 1) {
    // Inline paste: allow a single line delimited by the target char.
    items = (lines[0] ?? "")
      .split(delimiter)
      .map(stripMarker)
      .filter(Boolean);
  } else {
    items = lines.map(stripMarker).filter(Boolean);
    const offending = items.filter((p) => p.includes(delimiter));
    if (offending.length) {
      errors.push(
        `A list item can't contain "${delimiter}": ${offending.map((p) => `"${p}"`).join(", ")}`
      );
    }
  }
  if (hadContent && items.length === 0) {
    errors.push("No usable items remained after sanitation.");
  }
  return { items, errors };
}

// Run all four delimited fields at once. Returns the normalized values plus a
// flat list of {field, message} errors and a human-readable preview map.
export function sanitizeItemFields(form) {
  const description = sanitizeDescription(form.description);
  const features = sanitizeList(form.features, ";");
  const parts = sanitizeList(form.parts, "|");
  const materials = sanitizeList(form.materials, "|");
  const errors = [
    ...description.errors.map((message) => ({ field: "Description", message })),
    ...features.errors.map((message) => ({ field: "Features", message })),
    ...parts.errors.map((message) => ({ field: "Parts", message })),
    ...materials.errors.map((message) => ({ field: "Materials", message })),
  ];
  return {
    description: description.value,
    features: features.items,
    parts: parts.items,
    materials: materials.items,
    errors,
    preview: {
      Description: description.value || "(empty)",
      Features: features.items.join(" ; ") || "(empty)",
      Parts: parts.items.join(" | ") || "(empty)",
      Materials: materials.items.join(" | ") || "(empty)",
    },
  };
}

// Render-side: turn a stored description back into paragraphs. New data is
// ";"-joined; legacy rows may be "\n"-joined or an array of lines.
export function splitDescription(stored) {
  if (Array.isArray(stored)) return stored.map((s) => String(s).trim()).filter(Boolean);
  const s = typeof stored === "string" ? stored : "";
  if (!s) return [];
  const parts = s.includes(";") ? s.split(";") : s.split("\n");
  return parts.map((p) => p.trim()).filter(Boolean);
}

// ── Image filename / destination helpers ──────────────────────────────────────

export function imageExtFromName(name) {
  const m = /\.([a-z0-9]+)$/i.exec(String(name || "").trim());
  return m ? m[1].toLowerCase() : "";
}

export function imageExtFromUrl(url) {
  try {
    const { pathname } = new URL(url);
    return imageExtFromName(pathname);
  } catch {
    return imageExtFromName(url);
  }
}

// Deterministic {SKU}.{ext} target under the statically-served products dir.
// baseUrl prefixes the stored imageUrl so it matches what the server persists
// (absolute, pointing at the API origin); omit it for a root-relative path.
export function imageDestination(sku, ext, baseUrl = "") {
  const safeSku = String(sku || "").trim();
  const e = String(ext || "").replace(/^\./, "").toLowerCase();
  const fileName = `${safeSku}.${e}`;
  const base = String(baseUrl || "").replace(/\/$/, "");
  return {
    fileName,
    diskPath: `assets/products/${fileName}`,
    imageUrl: `${base}/assets/products/${fileName}`,
  };
}
